import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';
import { useIsVerticalToolbar } from 'components/shared/ToolbarSet/ToolbarPosition.context';

import { useAppointmentsFilterNames } from '../../hooks/useAppointmentsFilterNames';
import {
  SummaryButton,
  SummaryTooltipList
} from './AppointmentsOverviewFilterSummary.styles';

import type { AppointmentsOverviewFilterSummaryProps } from './AppointmentsOverviewFilterSummary.types';

/**
 * What the overview is filtered on, beside the Filters trigger: every applied person, plan unit and
 * activity by name, on one line, with the whole list on hover. Names rather than a count, since the page
 * is empty without a selection and a planner has to be able to read which one it is. "Nog niets gekozen"
 * while nothing is applied. Clicking it opens the panel.
 *
 * Left out when the toolbar runs as a column: there is no width for a line of names, and the trigger's
 * badge says there is a filter.
 */
const AppointmentsOverviewFilterSummary = ({
  selections,
  activityIds,
  onOpen
}: AppointmentsOverviewFilterSummaryProps) => {
  'use memo';

  const { t } = useTranslation('common');
  const isVerticalToolbar = useIsVerticalToolbar();
  // The same names a downloaded file prints in its header.
  const names = useAppointmentsFilterNames(selections, activityIds);

  if (isVerticalToolbar) return null;

  if (names.length === 0) {
    return (
      <SummaryButton muted onClick={onOpen}>
        {t('appointmentsOverview.filters.nothingSelected')}
      </SummaryButton>
    );
  }

  return (
    <Tooltip
      title={
        <SummaryTooltipList>
          {names.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </SummaryTooltipList>
      }
    >
      <SummaryButton onClick={onOpen}>{names.join(', ')}</SummaryButton>
    </Tooltip>
  );
};

export default AppointmentsOverviewFilterSummary;
